import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { VBox } from '../layout';
import { isNullOrUndefined } from '../../util';
import { LineChartTooltip } from './LineChartTooltip';
import type { LineChartProps, SingleLineData } from './LineChartUtil';
import {
  d3,
  getChartSvgProps,
  LineChartHeader,
  margin,
  renderRangeArea,
  SharedLineChartSetup,
  useChartLayoutEffect,
  useChartRefs,
  useScaledXAxis,
  useShowLoadingMask,
  useTooltipHandler,
  useTrackChartDimensions
} from './LineChartUtil';
import { BlankState, LoadMask } from '../basic';
import { intl } from '../../lib';

export type SingleLineChartProps = LineChartProps & {
  data: SingleLineData[];
};

export const SingleLineChart = ({
  data,
  title,
  infoModalTitle,
  infoModalContent,
  additionalComponent,
  initialWidth = 400,
  initialHeight = 250,
  loading = false,
  lowReference,
  highReference
}: Readonly<SingleLineChartProps>): React.JSX.Element => {
  const { legendRef, dotsGroupRef, svgContainerRef } = useChartRefs();
  const isMasked = useShowLoadingMask();
  const { width, height } = useTrackChartDimensions(
    svgContainerRef,
    initialWidth,
    initialHeight,
    !!additionalComponent,
    false
  );

  const x = useScaledXAxis(data, width);

  const y = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([
          (d3.min(data, d => d.y.firstLine.value) as number) * 0.9,
          (d3.max(data, d => d.y.firstLine.value) as number) * 1.1
        ])
        .range([height - margin.bottom, margin.top]),
    [data, height]
  );

  const { tooltipState, showTooltip, hideTooltip } = useTooltipHandler(
    data,
    dotsGroupRef,
    x,
    y,
    width
  );

  const line = useMemo(
    () =>
      d3
        .line<SingleLineData>()
        .defined(
          d =>
            !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
        )
        .x(d => x(new Date(d.x).valueOf()) as number)
        .y(d => y(d.y.firstLine.value) as number),
    [x, y]
  );

  useChartLayoutEffect(dotsGroupRef, showTooltip, hideTooltip);

  const filteredData = useMemo(() => {
    return data.filter(
      d => !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
    );
  }, [data]);

  return (
    <VBox className="line-chart-container" ref={svgContainerRef}>
      <LoadMask
        masked={isMasked || loading}
        className={loading ? 'chart-root' : ''}
      />
      <LineChartHeader
        title={title}
        infoModalTitle={infoModalTitle}
        infoModalContent={infoModalContent}
        additionalComponent={additionalComponent}
      />
      {data.length === 0 ? (
        <BlankState
          description={intl('common.noData')}
          iconName="database-no"
          scale="small"
        />
      ) : (
        <div className="ehs-chart-svg-container">
          <svg {...getChartSvgProps(width, height, true)}>
            <SharedLineChartSetup
              data={data}
              x={x}
              y={y}
              height={height}
              width={width}
              legendRef={legendRef}
            />
            {renderRangeArea(lowReference, highReference, y, height, width)}
            <g>
              <path
                transform={`translate(${margin.left}, 0)`}
                className="line"
                d={line(filteredData) ?? undefined}
              />
              <g ref={dotsGroupRef}>
                {data.map(d => (
                  <React.Fragment key={uuidv4()}>
                    {!isNullOrUndefined(d.x) && (
                      <circle
                        cx={x(new Date(d.x).valueOf())}
                        cy={y(d.y.firstLine.value)}
                        r={5}
                        fill="var(--secondary-color)"
                        transform={`translate(${margin.left}, 0)`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </g>
            </g>
            <LineChartTooltip
              {...tooltipState}
              showHoverGuidelines={true}
              width={width}
              height={height}
            />
          </svg>
        </div>
      )}
    </VBox>
  );
};
